/**
 * 🧹 Text Sanitization Utilities
 * 
 * ลบตัวอักษรพิเศษและ Unicode ที่ไม่ต้องการก่อนบันทึกลง Database
 */

/**
 * Zero-width characters
 */
const ZERO_WIDTH_REGEX = /[\u200B-\u200F\u2028-\u202F\u2060-\u206F\uFEFF]/g;

/**
 * Sanitize text: ลบ backtick, zero-width chars, และอักษรแปลกๆ
 * 
 * @example
 * sanitizeText("ส`ยูโอ22") // => "สยูโอ22"
 * sanitizeText("Hello\u200B World") // => "Hello World"
 */
export function sanitizeText(text: string | undefined | null): string {
  if (!text) return '';
  
  return text
    // Normalize Unicode (รวม combining characters)
    .normalize('NFC')
    // ลบ backtick และ grave accent
    .replace(/[`´''‛]/g, '')
    // ลบ zero-width characters
    .replace(ZERO_WIDTH_REGEX, '')
    // ลบ control characters (ยกเว้น newline และ tab)
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
    // Trim whitespace
    .trim();
}

/**
 * Sanitize ชื่อ (เข้มงวดกว่า - เอาแค่ตัวอักษรและตัวเลข)
 * ใช้สำหรับ firstName, lastName
 * 
 * @example
 * sanitizeName("ส`ยูโอ22") // => "สยูโอ22"
 * sanitizeName("  John  Doe  ") // => "John  Doe"
 */
export function sanitizeName(name: string | undefined | null): string {
  if (!name) return '';
  
  return name
    // Normalize
    .normalize('NFC')
    // ลบ backtick และ tick marks
    .replace(/[`´''‛`]/g, '')
    // ลบ grave accent (U+0060)
    .replace(/\u0060/g, '')
    // ลบ acute accent (U+00B4)
    .replace(/\u00B4/g, '')
    // ลบ zero-width chars
    .replace(ZERO_WIDTH_REGEX, '')
    // ลบ control chars
    .replace(/[\x00-\x1F\x7F]/g, '')
    // Trim
    .trim();
}

/**
 * Sanitize เบอร์โทรศัพท์ (เอาแค่ตัวเลข)
 * 
 * @example
 * sanitizePhone("081-234-5678") // => "0812345678"
 * sanitizePhone("+66 81 234 5678") // => "66812345678"
 */
export function sanitizePhone(phone: string | undefined | null): string {
  if (!phone) return '';
  return phone.replace(/\D/g, '');
}

/**
 * Sanitize เลขบัญชี (เอาแค่ตัวเลขและ dash)
 * 
 * @example
 * sanitizeBankAccount("123-4-56789-0") // => "123-4-56789-0"
 */
export function sanitizeBankAccount(account: string | undefined | null): string {
  if (!account) return '';
  return account.replace(/[^\d-]/g, '').trim();
}

/**
 * Sanitize object - ลบตัวอักษรพิเศษจากทุก string fields
 */
export function sanitizeObject<T extends Record<string, unknown>>(obj: T): T {
  const result = { ...obj } as Record<string, unknown>;
  
  for (const key of Object.keys(result)) {
    const value = result[key];
    
    if (typeof value === 'string') {
      // Use appropriate sanitizer based on field name
      if (key.includes('Name') || key.includes('name') || key === 'firstName' || key === 'lastName') {
        result[key] = sanitizeName(value);
      } else if (key.includes('phone') || key.includes('Phone') || key.includes('tel') || key.includes('Tel')) {
        result[key] = sanitizePhone(value);
      } else if (key.includes('bankAccount') || key.includes('bank_account')) {
        result[key] = sanitizeBankAccount(value);
      } else {
        result[key] = sanitizeText(value);
      }
    }
  }
  
  return result as T;
}

export default {
  sanitizeText,
  sanitizeName,
  sanitizePhone,
  sanitizeBankAccount,
  sanitizeObject,
};





